import React from 'react';
import { Helmet } from 'react-helmet';
import { Form, Input, Button, Card, Typography, Alert, message } from 'antd';
import { MailOutlined, ReloadOutlined } from '@ant-design/icons';
import MainModel from '../../../../model/MainModel';
import * as API_USER from '../../../../shared/http/user';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = React.useState(false);
    const [cooldown, setCooldown] = React.useState(0);      // секунды до повторной отправки
    const [success, setSuccess] = React.useState<string>('');
    const [error, setError] = React.useState<string>('');
    const [msg, ctx] = message.useMessage();

    // тикер для кулдауна
    React.useEffect(() => {
        if (cooldown <= 0) return;
        const id = setInterval(() => setCooldown((s) => s - 1), 1000);
        return () => clearInterval(id);
    }, [cooldown]);

    const onFinish = async ({ email }: { email: string }) => {
        setSuccess('');
        setError('');
        try {
            setSubmitting(true);
            await API_USER.resetPassword(email);
            setSuccess('Письмо отправлено. Повторный запрос будет доступен через минуту.');
            setCooldown(60);
            msg.success('Письмо отправлено');
        } catch (e: any) {
            const m = MainModel.getMessageFromGeekException?.(e) || e?.message || 'Не удалось отправить письмо';
            setError(m);
            msg.error(m);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            id="reset-password"
            style={{
                minHeight: '100dvh',
                display: 'grid',
                placeItems: 'center',
                padding: 16,
                background: 'linear-gradient(135deg, rgba(99,102,241,.08), rgba(236,72,153,.08))',
            }}
        >
            <Helmet title="Voxlingua: Сброс пароля" />
            {ctx}

            <Card style={{ width: '100%', maxWidth: 440 }}>
                <Title level={3} style={{ marginBottom: 8 }}>Voxlingua</Title>
                <Text type="secondary">Сброс пароля</Text>

                <Form
                    form={form}
                    layout="vertical"
                    size="large"
                    style={{ marginTop: 16 }}
                    onFinish={onFinish}
                    requiredMark={false}
                >
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Введите email' },
                            { type: 'email', message: 'Некорректный email' },
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined />}
                            placeholder="you@example.com"
                            inputMode="email"
                            autoComplete="email"
                        />
                    </Form.Item>

                    {success && <Alert type="success" showIcon message={success} style={{ marginBottom: 12 }} />}
                    {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

                    <Form.Item shouldUpdate noStyle>
                        {() => {
                            const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
                            const touched = form.isFieldsTouched(['email'], true);
                            const disabled = submitting || cooldown > 0 || hasErrors || !touched;

                            return (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={submitting}
                                    disabled={disabled}
                                    icon={<ReloadOutlined spin={submitting} />}
                                >
                                    {cooldown > 0 ? `Отправить снова через ${cooldown}s` : 'Сбросить пароль'}
                                </Button>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default ResetPassword;
